import type { IImage } from '@/types/image';
import { isEmpty } from 'lodash';
import { array, mixed, number, object, string } from 'yup';
import { inventoryStatusValues } from '@/features/inventory/inventory.constants';

/**
 * Active, inactive or suspended (what Delete writes), picked with the Smart
 * Suggestion status select. Not a switch: a truthy transform read `suspended` as
 * "on" and saved a deleted record back as active.
 */
const recordStatus = string().oneOf(inventoryStatusValues).default('active');

const taxSettingValidationSchema = object({
  name: string().required().label('Name'),
  percentage: number().required().label('Percentage'),
  status: mixed().transform(el => (el ? 'active' : 'inactive')),
});

const contactDetailValidationSchema = object({
  firstName: string().required().label('First Name'),
  lastName: string().required().label('Last Name'),
  email: string().email().required().label('Email'),
  phoneNumber: string().required().label('Phone Number'),
  street: string().required().label('Street'),
  houseNumber: string().required().label('House Number'),
  suffix: string().required().label('Suffix'),
  city: string().required().label('City'),
  state: string().required().label('State'),
  zipcode: string().required().label('Zipcode'),
  country: string().required().label('Country'),
  cocNumber: string().optional().label('COC Number'),
  vatNumber: string().optional().label('VAT Number'),
});

const resellerSettingValidationSchema = object({
  companyId: string().required().label('Company ID'),
  branchId: array()
    .of(string().required())
    .min(1, 'Branch is required')
    .required()
    .label('Branch'),
  name: string().required().label('Name'),
  contactPerson: string().required().label('Contact Person'),
  email: string().email().required().label('Email'),
  phoneNumber: string().required().label('Phone Number'),
  status: recordStatus,
});

/**
 * `companyId` is only honoured for a super Admin — for anyone else the API
 * falls back to the caller's own company. It is still sent on create because
 * POST /group-type accepts it; PUT /group-type/:id does not, so the edit
 * payload is narrowed to name + status in the service.
 */
const groupTypeSettingValidationSchema = object({
  companyId: string().required().label('Company ID'),
  name: string().required().label('Name'),
  status: recordStatus,
});

const promoBannerValidationSchema = object({
  title: string().required().label('Name'),
  targetUrl: string().required().label('Link'),
  desc: string().optional(),
  images: mixed()
    .transform((el: IImage[] | undefined) => {
      return isEmpty(el) ? [] : el?.map(item => item.name);
    })
    .required()
    .default(null)
    .label('Image'),
});

/**
 * A euro amount the field commits as text: blank is "no bound" (`null`), not 0.
 */
const giftCardAmountBound = mixed()
  .transform((value: unknown) =>
    value === '' || value == null ? null : Number(value)
  )
  .test(
    'positive-amount',
    'Enter an amount above 0',
    value =>
      value == null ||
      (typeof value === 'number' && Number.isFinite(value) && value > 0)
  )
  .nullable()
  .default(null);

const companyDetailValidationSchema = object({
  // Single logo file -> API expects the stored filename string. An emptied
  // field goes out as `null`, not `undefined`: JSON drops an undefined key,
  // and the API only overwrites the keys it is sent, so a removed logo would
  // quietly stay on the record.
  logo: mixed()
    .transform((el: IImage[] | undefined) =>
      isEmpty(el) ? null : (el?.[0]?.name ?? null)
    )
    .required()
    .label('Company logo'),
  companyName: string().required().label('Company name'),
  cocNumber: string().required().label('KvK number'),
  address: string().required().label('Address'),
  vatNumber: string().required().label('VAT ID'),
  postalCode: string().required().label('Postal code'),
  place: string().required().label('City'),
  country: string().required().label('Country'),
  // Required: a Betaaloverzicht asks to be paid into it, and there is no other
  // company's account to fall back on (ELI-360).
  iban: string().trim().required().label('IBAN'),
  bankName: string().required().label('Bank name'),
  email: string().email().required().label('Email'),
  phoneNumber: string().required().label('Phone number'),
  // Free text rather than `.url()`: operators type `eliio.nl` as often as a
  // full URL, and the gift card prints it without the scheme either way.
  website: string().trim().required().label('Website'),
  // A real link, unlike `website`: customers click it from the widget checkout.
  term: string()
    .trim()
    .url('Enter a full link, starting with https://')
    .max(255)
    .required()
    .label('Terms and conditions link'),
  paymentTerm: number()
    .typeError('Enter a number of days')
    .integer()
    .min(0)
    .required()
    .label('Payment term'),
  // Gift card artwork. Same single-file -> filename contract as `logo` —
  // `null` when removed included — and optional: a company without gift card
  // branding just never sets it.
  giftCardImage: mixed()
    .transform((el: IImage[] | undefined) =>
      isEmpty(el) ? null : (el?.[0]?.name ?? null)
    )
    .nullable()
    .optional()
    .label('Gift card image'),
  // Bounds on the widget's custom gift card amount. Optional: empty goes out as
  // `null`, which the widget reads as its default 10 / 500. Sent as `null`
  // rather than left out for the same reason as `logo`: a cleared bound must
  // overwrite the stored one.
  giftCardMinAmount: giftCardAmountBound.label('Minimum amount'),
  giftCardMaxAmount: giftCardAmountBound
    .test(
      'max-at-least-min',
      'Maximum must be at least the minimum',
      (max, context) => {
        const min: unknown = context.parent.giftCardMinAmount;
        return typeof max !== 'number' || typeof min !== 'number' || max >= min;
      }
    )
    .label('Maximum amount'),
});

const googleCalendarShareValidationSchema = (isEdit = false) =>
  object({
    email: string().email().required().label('Email'),
    subCalendars: isEdit
      ? array().of(string().required()).default([]).label('Sub Calendars')
      : array()
          .of(string().required())
          .min(1, 'Select at least one sub calendar')
          .required()
          .label('Sub Calendars'),
  });

// Both fields are optional: the API only touches the keys it receives, so an
// empty field means "leave the stored secret as it is". Clearing a value is done
// through the delete endpoint, not by submitting an empty string.
const companyConfigValidationSchema = object({
  stripeSecretKey: string().default('').label('Stripe secret key'),
  stripeWebhookSecret: string().default('').label('Stripe webhook secret'),
});

export {
  taxSettingValidationSchema,
  contactDetailValidationSchema,
  resellerSettingValidationSchema,
  groupTypeSettingValidationSchema,
  promoBannerValidationSchema,
  googleCalendarShareValidationSchema,
  companyDetailValidationSchema,
  companyConfigValidationSchema,
};
